import { cn } from '@/lib/utils';

/**
 * TabsPills — canonical horizontal pill-tab row.
 *
 * Replaces ~8 hand-rolled copies across the admin app (audited 2026-06-13)
 * that all had subtle visual drift in pad / size / hover tone and — most
 * importantly — used a bare `text-white` instead of `!text-white` for the
 * active state. Without the bang, body color inheritance + Slot wrapping
 * in some contexts beat the `text-white` color in the cascade and the
 * active pill text appeared dark on the dark navy primary background
 * (same root cause as the Button primary-variant fix in v0.9.x).
 *
 * Usage:
 *   <TabsPills
 *     value={tab}
 *     onChange={setTab}
 *     options={[
 *       { value: 'staff', label: 'Staff' },
 *       { value: 'attendance', label: 'Attendance', count: 12 }
 *     ]}
 *     size="md"
 *   />
 */

export interface TabsPillsOption {
  value: string;
  label: string;
  /** Optional inline count badge next to the label. */
  count?: number;
}

export interface TabsPillsProps {
  value: string;
  onChange: (value: string) => void;
  options: ReadonlyArray<TabsPillsOption>;
  /** sm = compact (text-vt-12, py-1, px-vt-3). md = default (text-vt-13, py-vt-1, px-vt-4). */
  size?: 'sm' | 'md';
  className?: string;
  /** Accessible label for the radiogroup-style row. */
  'aria-label'?: string;
}

const SIZE_CLS: Record<NonNullable<TabsPillsProps['size']>, string> = {
  sm: 'h-7 px-vt-3 py-1 text-vt-12',
  md: 'h-8 px-vt-4 py-vt-1 text-vt-13'
};

const BADGE_SIZE_CLS: Record<NonNullable<TabsPillsProps['size']>, string> = {
  sm: 'ml-vt-1 min-w-[16px] px-1 py-0 text-[10px] leading-[14px]',
  md: 'ml-vt-2 min-w-[18px] px-vt-1 py-0 text-vt-11 leading-[16px]'
};

export function TabsPills({
  value,
  onChange,
  options,
  size = 'md',
  className,
  'aria-label': ariaLabel
}: TabsPillsProps): JSX.Element {
  const sizeCls = SIZE_CLS[size];
  const badgeCls = BADGE_SIZE_CLS[size];
  return (
    <div
      role="group"
      aria-label={ariaLabel}
      className={cn('flex flex-wrap items-center gap-vt-2', className)}
    >
      {options.map((opt) => {
        const active = opt.value === value;
        return (
          <button
            key={opt.value}
            type="button"
            onClick={() => onChange(opt.value)}
            aria-pressed={active}
            className={cn(
              'inline-flex items-center justify-center whitespace-nowrap rounded-vt-pill font-medium transition-[background-color,color,box-shadow] duration-vt ease-vt focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-vt-primary-400 focus-visible:ring-offset-2 focus-visible:ring-offset-vt-bg',
              sizeCls,
              // !text-white on active — see file header comment for why the
              // bang is mandatory. Mirrors Button primary variant pattern.
              active
                ? 'bg-vt-primary !text-white shadow-vt-1 hover:bg-vt-primary-600'
                : 'bg-vt-surface-2 text-vt-text hover:bg-vt-surface-3'
            )}
          >
            <span>{opt.label}</span>
            {typeof opt.count === 'number' ? (
              <span
                className={cn(
                  'inline-flex items-center justify-center rounded-vt-pill font-semibold',
                  badgeCls,
                  active
                    ? 'bg-white/20 !text-white'
                    : 'bg-vt-surface text-vt-text-muted ring-1 ring-inset ring-vt-border'
                )}
                aria-hidden="true"
              >
                {opt.count}
              </span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}
